import React, { Fragment, useContext, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { AppContext } from '@prisjakt/ui-app-context';

import DotsVerticalIcon from 'mdi-react/DotsVerticalIcon';
import ChevronDown from 'mdi-react/ChevronDownIcon';

import Button from '@prisjakt/ui-button';
import Lazy from '@prisjakt/ui-lazy';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';

import Placeholder from './placeholder';
import PriceFilters from './price-filters/price-filters';
import PriceInformation from '../price-information';
import PriceRow, { columnCount } from '../../../../components/price-row';
import QuickPriceFilters from './quick-price-filters/quick-price-filters';

import {
  productPriceClick,
  productClickOutBounce,
} from '../../actions/product';

import { getFilter } from './utils';

import {
  useNonFeaturedStoreLinkExperiment,
  useShowStoreProductNamesExperiment,
} from '../../../../hooks/experiments';

import {
  BadgesFragment,
  Price,
  PriceListFragment,
  ProductPageFragment,
  ProductMediaFragment,
} from '../../../../types';

import * as S from './styles';
import { CurrentCampaign, PriceNode } from '../../types';

type OwnProps = {
  id: number;
  adOffset?: number;
  brand?: ProductPageFragment['brand'] | null;
  campaign?: CurrentCampaign | null;
  category?: ProductPageFragment['category'] | null;
  media?: ProductMediaFragment['media'];
  prices?: PriceListFragment['prices'];
  productName?: string;
  storeBadges?: BadgesFragment['storeBadges'];
  visiblePrices?: number;
};

type DispatchProps = {
  dispatchProductPriceClick: (
    id: number,
    price: Price | number,
    featured: boolean,
    status: string
  ) => void;
  dispatchProductClickOutBounce: (id: number, priceId?: string | null) => void;
};

type Props = OwnProps & DispatchProps;

const PriceList = ({
  id,
  adOffset = 0,
  brand,
  campaign,
  category,
  media,
  prices,
  productName,
  storeBadges = [],
  visiblePrices = 50,
  dispatchProductPriceClick,
  dispatchProductClickOutBounce,
}: Props) => {
  const { t, market } = useContext(AppContext);
  const [state, setState] = useState({
    showMorePrices: false,
    showInternational: false,
    showUsedProducts: false,
    showNotInMintCondition: false,
    showUnavailable: false,
    showPickupOnly: true,
  });
  const [featuredAlternativePriceType] = useState<string>(
    market.priceList && market.priceList.featuredAlternativePriceType
      ? market.priceList.featuredAlternativePriceType
      : 'international'
  );
  const [hiddenListOptions] = useState<string[]>(
    market.priceList && market.priceList.hiddenListOptions
      ? [...market.priceList.hiddenListOptions]
      : []
  );
  const [reportRowId, setReportRowId] = useState<string | null>(null);

  const { showStoreProductNames } = useShowStoreProductNamesExperiment();
  const { hideNonFeaturedStoreLinks } = useNonFeaturedStoreLinkExperiment();

  const advertSpacing = { top: '10px', bottom: '10px' };

  const renderPlaceholders = (count: number) => {
    return [...new Array(count)].map((_val, index) => (
      <Placeholder key={`placeholder-${index.toString()}`} />
    ));
  };

  const handlePriceFilterCheckbox = useCallback(
    e => {
      setState({
        ...state,
        [e.target.name]: e.target.checked,
      });
    },
    [state]
  );

  const onMorePrices = () => {
    setState({
      ...state,
      showMorePrices: true,
    });
  };

  const getPrices = () => {
    if (!prices || !prices.nodes) {
      return null;
    }

    return prices.nodes.filter(price => {
      // Check first if the store's market is allowed
      if (
        !price ||
        !price.store ||
        (price.store.market !== 'primary' && !state.showInternational)
      ) {
        return false;
      }

      // And then check the junk type
      switch (price.type) {
        case 'not_in_mint_condition':
          return state.showNotInMintCondition;
        case 'not_available_for_purchase':
          return state.showUnavailable;
        case 'used_product':
          return state.showUsedProducts;
        case 'pickup_only':
          return state.showPickupOnly;
        default:
          return true;
      }
    });
  };

  const filteredPrices = getPrices();
  if (
    filteredPrices &&
    filteredPrices.length === 0 &&
    !state.showUsedProducts
  ) {
    // If no prices are visible, include used products
    setState({ ...state, showUsedProducts: true });
  }

  const getMobileAd = (index: number) => {
    return (
      <S.AdvertTableRowElement key={`mobile-ad-container-${index}`}>
        <td colSpan={columnCount}>
          <S.AdContainer
            placement={`dubbelpanorama${Math.min(3, index + 1)}`}
            min={0}
            max={860}
            spacing={advertSpacing}
          />
        </td>
      </S.AdvertTableRowElement>
    );
  };

  const getDesktopAd = (index: number) => {
    const desktopAds = ['panorama_extra', 'panorama_extra_2'];
    const placement = desktopAds[Math.min(desktopAds.length - 1, index)];
    return (
      <S.AdvertTableRowElement
        withPosition
        key={`desktop-ad-container-${index}`}
        className="pj-ui-pricelist--advert"
      >
        <td colSpan={columnCount}>
          <S.AdContainer
            placement={placement}
            min={860}
            max={10000}
            key={`${placement}-${index}`}
            scalable
            scaleBase=".pj-ui-pricelist--advert"
            spacing={advertSpacing}
          />
        </td>
      </S.AdvertTableRowElement>
    );
  };

  const insertTableAds = (tableRows: Price[]) => {
    const adsIndexes = [3, 9, 18, 36, 54, 72, 90, 108]
      .map(index => index + adOffset)
      .filter(index => index <= tableRows.length);
    if (!adsIndexes.length) {
      // special case for short list
      tableRows.splice(tableRows.length, 0, getDesktopAd(0));
      tableRows.splice(tableRows.length, 0, getMobileAd(0));
      return tableRows;
    }
    for (let i = adsIndexes.length - 1; i >= 0; i -= 1) {
      const index = adsIndexes[i];
      // Only include two desktop ads
      if (i <= 1) {
        tableRows.splice(index, 0, getDesktopAd(i));
      }
      tableRows.splice(index, 0, getMobileAd(i));
    }
    return tableRows;
  };

  const {
    hasInternationalPrices,
    hasUsedProducts,
    hasNotInMintCondition,
    hasPickupOnly,
    hasUnavailable,
    hasFilter,
  } = getFilter(prices);

  const priceFilters = [
    {
      name: 'showInternational',
      checked: state.showInternational,
      hasPrices: hasInternationalPrices,
      showAsQuickFilter: featuredAlternativePriceType === 'international',
      label: t('product', 'price-filter.international'),
      shortLabel: t('product', 'price-filter.international-short'),
      hide: false,
    },
    {
      name: 'showUsedProducts',
      checked: hasUsedProducts && state.showUsedProducts,
      hasPrices: hasUsedProducts,
      showAsQuickFilter: featuredAlternativePriceType === 'used_product',
      label: t('product', 'price-filter.used'),
      hide: false,
    },
    {
      name: 'showNotInMintCondition',
      checked: hasNotInMintCondition && state.showNotInMintCondition,
      label: t('product', 'price-filter.not-in-mint-condition'),
      hasPrices: hasNotInMintCondition,
      showAsQuickFilter: false,
      hide: Boolean(
        hiddenListOptions.filter(option => option === 'not_in_mint_condition')
          .length
      ),
    },
    {
      name: 'showPickupOnly',
      checked: hasPickupOnly && state.showPickupOnly,
      label: t('product', 'price-filter.pickup-only'),
      hasPrices: hasPickupOnly,
      showAsQuickFilter: false,
      hide: Boolean(
        hiddenListOptions.filter(option => option === 'pickup_only').length
      ),
    },
    {
      name: 'showUnavailable',
      checked: hasUnavailable && state.showUnavailable && true,
      label: t('product', 'price-filter.not-available-for-purchase'),
      hasPrices: hasUnavailable,
      showAsQuickFilter: false,
      hide: false,
    },
  ];

  const quickFilters = priceFilters.filter(elem => elem.showAsQuickFilter);

  // This experiment hides store link if store is non-featured.
  // 'externalUri' determines if link is shown or not.

  const shownPrices = state.showMorePrices
    ? filteredPrices
    : filteredPrices && filteredPrices.slice(0, visiblePrices);

  const currentPrices =
    hideNonFeaturedStoreLinks && shownPrices
      ? shownPrices.map(price =>
          price && price.store && price.store.featured
            ? price
            : ({ ...price, externalUri: null } as PriceNode)
        )
      : shownPrices;

  const lazyPlaceholderStyle = { height: 57 };

  const lazyPlaceholder = (
    <tr>
      <td colSpan={columnCount} style={lazyPlaceholderStyle} />
    </tr>
  );

  const wrapLazy = (
    order: number,
    rowKey: string,
    component: React.ReactElement
  ) => {
    if (order > 10) {
      return (
        <Lazy placeholder={lazyPlaceholder} offset={200} key={rowKey}>
          {component}
        </Lazy>
      );
    }
    return component;
  };

  const renderPriceRow = (item: Price, index: number) => {
    const sortedStoreBadges = storeBadges
      ? storeBadges
          .filter(badge => badge && badge.itemId)
          .sort((a, b) => b!.nrOfPositiveReviews - a!.nrOfPositiveReviews)
          .reduce((acc, badge) => {
            acc[badge!.itemId!] = acc[badge!.itemId!] || [];
            acc[badge!.itemId!].push(badge);
            return acc;
          }, {})
      : [];

    const rowKey = `${item.id}-${index}`;

    return wrapLazy(
      index,
      rowKey,
      <PriceRow
        key={rowKey}
        order={index + 1}
        media={media}
        productId={id}
        productName={productName}
        priceId={item.id}
        priceType={item.type}
        price={item.price}
        reportRowId={reportRowId}
        setReportRowId={setReportRowId}
        store={item.store}
        storeInfo={
          item.store && item.store.providedByStore
            ? item.store.providedByStore.generalInformation
            : null
        }
        storeBadges={item.store ? sortedStoreBadges[item.store.id] : []}
        stock={item.stock}
        externalUri={item.externalUri}
        brand={brand}
        alternativePrices={item.alternativePrices}
        openInNewWindow
        knownShippingPrice={
          item.price ? Boolean(item.price.inclShipping) : false
        }
        brandRecommendedRetailer={item.brandRecommendedRetailer}
        isInternationalPrice={
          state.showInternational &&
          item.store &&
          item.store.market === 'secondary'
        }
        showInternational={state.showInternational}
        campaignStyling={
          campaign &&
          campaign.media &&
          campaign.price &&
          item &&
          item.id === campaign.price.id
            ? campaign.media.colors
            : null
        }
        onPriceClick={(featured: boolean, price: number, status: string) => {
          // NOTE: Any changes done to data or structure of PRODUCT_PRICE_CLICK needs to be
          // reflected in react/middlewares/out pre click for tracking clicks before JS bundle
          dispatchProductPriceClick(id, price || item, featured, status);
        }}
        onClickOutBounce={dispatchProductClickOutBounce}
        showStoreProductNames={showStoreProductNames}
      />
    );
  };

  return (
    <Panel
      header={
        <S.Header>
          <PanelHeader
            title={
              <Fragment>
                <S.OnlyDesktop data-test="SortOrder">
                  {t('product', 'prices.title')}
                </S.OnlyDesktop>
              </Fragment>
            }
            divider="none"
          >
            <QuickPriceFilters
              priceFilters={quickFilters}
              onFilterChanged={handlePriceFilterCheckbox}
            />
            {hasFilter && (
              <S.PriceFilter
                title={
                  <Fragment>
                    <S.OnlyMobile>
                      <DotsVerticalIcon size={20} />{' '}
                    </S.OnlyMobile>
                    <S.OnlyDesktop>
                      {t('product', 'prices.more-filters')}{' '}
                    </S.OnlyDesktop>
                  </Fragment>
                }
                type="custom-content-type"
                headerPosition="right"
                ariaLabel={t('product', 'prices.more-filters')}
                data-test="PriceFilters"
              >
                <PriceFilters
                  priceFilters={priceFilters}
                  onFilterChanged={handlePriceFilterCheckbox}
                />
              </S.PriceFilter>
            )}
          </PanelHeader>
        </S.Header>
      }
      footer={
        <div>
          {market.showProductInformationTexts &&
            currentPrices &&
            currentPrices.length > 0 && (
              <S.Information>
                {t('product', 'price-list.data-information')}
              </S.Information>
            )}
          {currentPrices &&
            filteredPrices &&
            currentPrices.length < filteredPrices.length && (
              <Fragment>
                <Button
                  style={{ width: '100%' }}
                  iconPosition="bottom"
                  icon={<ChevronDown />}
                  variant="text"
                  color="link"
                  onClick={() => onMorePrices()}
                >
                  {t('product', 'prices.show-more-prices-button')}
                </Button>
              </Fragment>
            )}
        </div>
      }
    >
      {prices && prices.meta && prices.meta.storeStatistics && (
        <PriceInformation storeStatistics={prices.meta.storeStatistics} />
      )}
      {currentPrices === null && renderPlaceholders(5)}
      {currentPrices && currentPrices.length === 0 && (
        <S.NoPrices key="no-prices">
          <div>
            {t('product', 'prices.no-prices')}
            {category && (
              <div>
                {t('product', 'prices.view-all-in-category-link')}{' '}
                <Link to={category.pathName}>{category.name}</Link>
              </div>
            )}
          </div>
        </S.NoPrices>
      )}
      <S.PriceRowTable>
        <thead>
          <tr>
            <S.PriceRowTableHeadExpand />
            <S.PriceRowTableHeadStore scope="col">
              <div>{t('product', 'price-list.header-store')}</div>
            </S.PriceRowTableHeadStore>
            <S.PriceRowTableHeadProduct scope="col">
              <div>
                {showStoreProductNames
                  ? t('product', 'price-list.header-product')
                  : ' '}
              </div>
            </S.PriceRowTableHeadProduct>
            <S.PriceRowTableHeadStock scope="col">
              <div>{t('product', 'price-list.header-stock')}</div>
            </S.PriceRowTableHeadStock>
            <S.PriceRowTableHeadPrice scope="col">
              <div>{t('product', 'price-list.header-price')}</div>
            </S.PriceRowTableHeadPrice>
            <S.PriceRowTableHeadClickOut />
          </tr>
        </thead>
        <tbody>
          {currentPrices &&
            currentPrices.length > 0 &&
            insertTableAds(
              currentPrices.map((item, index) =>
                renderPriceRow(item as Price, index)
              )
            )}
        </tbody>
      </S.PriceRowTable>
    </Panel>
  );
};

const dispatchProps = {
  dispatchProductPriceClick: productPriceClick,
  dispatchProductClickOutBounce: productClickOutBounce,
};

export default connect(null, dispatchProps)(PriceList);
